import { danoniKeysConfig, EventLineType, ImportExportEventType } from "../constants"
import { timeToFraction } from "../utilFunctions"

// --------------------------------------------------------
// EXPORT
// --------------------------------------------------------

export const exportHandlerDanoniSkbEditor = {
  init: (chartObj) => {
    const { chartParameters: chartParameters_ } = chartObj
    const chartParameters = chartParameters_.value
    const blankFrame = chartParameters.blankFrame == null || isNaN(Number(chartParameters.blankFrame)) ? 200 : chartParameters.blankFrame
    const scoreNumber = chartParameters.scoreNumber == null || isNaN(parseInt(chartParameters.scoreNumber)) ? 1 : parseInt(chartParameters.scoreNumber)
    const scorePrefix = chartParameters.scorePrefix == null ? "" : parseInt(chartParameters.scorePrefix)
    const keyKind = chartParameters.keyKind == null ? "7" : chartParameters.keyKind
    const state = {
      result: {
        "keyKind": keyKind,
        "scoreNumber": scoreNumber,
        "scorePrefix": scorePrefix,
        "blankFrame": blankFrame,
      },
      openedFrzList: [],
      currentCl: -1,
    }

    // timings
    let currentPage = 1
    state.result["timings"] = chartObj.orderedCL.value.map((e, i) => {
      let p = parseInt(e.eventLine.pageBlockNum)
      p = isNaN(p) ? 8 : p
      const prev = chartObj.orderedCL.value[i - 1]
      if (prev) {
        currentPage = Math.trunc(prev.durationInBeats / (prev.eventLine.pageBlockNum ?? 8)) + currentPage
      }

      return {
        "label": currentPage,
        "startNum": e.eventLine.time * 60,
        "bpm": e.eventLine.bpm,
        "pageBlockNum": p
      }
    })

    let keyConfig
    try {
      keyConfig = JSON.parse(chartParameters.keyConfig)
      if (keyConfig == null || keyConfig == "") keyConfig = null
    } catch (e) {
      if (!(e instanceof SyntaxError)) throw e
      keyConfig = null
    }
    if (!keyConfig) keyConfig = danoniKeysConfig[keyKind] ?? []
    state.columnCount = keyConfig.names.length

    // scores
    state.result["scores"] =
      chartObj.orderedCL.value.reduce((a, e, i) => {
        return a.concat(
          Array.from({ length: Math.ceil(((e.endTime - e.eventLine.time) * e.eventLine.bpm / 60) / state.result["timings"][i]["pageBlockNum"]) },
            () => ({
              "notes": Array.from({ length: keyConfig.names.length }, () => []),
              "freezes": Array.from({ length: keyConfig.names.length }, () => []),
              "speeds": []
            }))
        )
      }, [])
    return state
  },
  processEvent: (event, state, chartObj) => {
    if (event.type === ImportExportEventType.EventLine) {
      if (event.value.type === EventLineType.Timing) {
        state.currentCl += 1
      }
      const currCl = Math.max(0, state.currentCl)
      const pageBlockNum = state.result["timings"][currCl]["pageBlockNum"]
      const label = state.result["timings"][currCl]["label"] - 1
      const speed = Number(event.value.speed)
      if (!isNaN(speed)) {
        const beat = (event.value.time - chartObj.orderedCL.value[currCl].eventLine.time) * chartObj.orderedCL.value[currCl].eventLine.bpm / 60
        const speedObj = {
          position: Math.round(beat % pageBlockNum * 24) * 2,
          value: speed,
          type: "speed"
        }
        state.result["scores"][Math.trunc(Math.trunc(beat) / pageBlockNum) + label]["speeds"].push(speedObj)
      }

      const boost = Number(event.value.boost)
      if (!isNaN(boost)) {
        const beat = (event.value.time - chartObj.orderedCL.value[currCl].eventLine.time) * chartObj.orderedCL.value[currCl].eventLine.bpm / 60
        const boostObj = {
          position: Math.round(beat % pageBlockNum * 24) * 2,
          value: boost,
          type: "boost"
        }
        state.result["scores"][Math.trunc(Math.trunc(beat) / pageBlockNum) + label]["speeds"].push(boostObj)
      }


    } else if (event.type === ImportExportEventType.Note) {
      const note = event.value
      const pageBlockNum = state.result["timings"][state.currentCl]["pageBlockNum"]
      const label = state.result["timings"][state.currentCl]["label"] - 1
      let beatIndex = Math.trunc(note.beat)
      const columnIndex = note.column.columnIndex
      if(columnIndex >= state.columnCount) return
      if (state.openedFrzList[note.column.columnIndex]) {
        state.result["scores"][Math.trunc(beatIndex / pageBlockNum + label)]["freezes"][columnIndex].push(Math.round(note.beat % pageBlockNum * 24) * 2)
        state.openedFrzList[note.column.columnIndex] = false
      } else {
        if (note.long) {
          state.result["scores"][Math.trunc(beatIndex / pageBlockNum + label)]["freezes"][columnIndex].push(Math.round(note.beat % pageBlockNum * 24) * 2)
          state.openedFrzList[note.column.columnIndex] = true
        } else {
          state.result["scores"][Math.trunc(beatIndex / pageBlockNum + label)]["notes"][columnIndex].push(Math.round(note.beat % pageBlockNum * 24) * 2)
        }
      }
    }
  },
  finally: (state, _chartObj) => {
    return JSON.stringify(state.result)
  }
}

// --------------------------------------------------------
// IMPORT 
// --------------------------------------------------------

export function importFunctionDanoniSkbEditor(chartObj, dataStr) {
  const dataObj = JSON.parse(dataStr)
  const { chartParameters } = chartObj

  let keyKind = dataObj.keyKind
  let blankFrame = dataObj.blankFrame

  if (dataObj.keyKind != null) {
    chartObj.setChartParameter('keyKind', dataObj.keyKind)
  } else if (chartObj.chartParameters.value.keyKind != null) {
    keyKind = chartObj.chartParameters.value.keyKind
  }
  if (dataObj.blankFrame != null) {
    chartObj.setChartParameter('blankFrame', dataObj.blankFrame)
  } else if (chartObj.chartParameters.value.blankFrame != null) {
    blankFrame = chartObj.chartParameters.value.blankFrame
  } else {
    blankFrame = 200
  }
  if (dataObj.scoreNumber != null) {
    chartObj.setChartParameter('scoreNumber', dataObj.scoreNumber)
  }
  if (dataObj.scorePrefix != null) {
    chartObj.setChartParameter('scorePrefix', dataObj.scorePrefix)
  }

  const labelMap = new Map()
  let hasValidLabel = false
  for (let labelIndex = 0; labelIndex < dataObj.timings?.length ?? 0; labelIndex++) {
    const { label, startNum, bpm, pageBlockNum } = dataObj.timings[labelIndex]
    if (!Number.isInteger(label) || !isFinite(startNum) || !isFinite(bpm) || !Number.isInteger(pageBlockNum)) continue
    hasValidLabel = true
    const t = startNum / 60
    let eventLine = chartObj.orderedCL.value.find(e => e.eventLine.time == t && e.eventLine.bpm == bpm)?.eventLine
    if (eventLine) {
      chartObj.setEventLineParameter(eventLine.eventLineId, 'pageBlockNum', pageBlockNum)
    } else {
      eventLine = chartObj.addEventLine(t, EventLineType.Timing, { bpm, pageBlockNum })
    }
    labelMap.set(label, eventLine)
  }
  if (!hasValidLabel) throw new Error("Timing section has no valid elements!")

  let columnNames
  try {
    const keyConfig = JSON.parse(chartParameters.keyConfig)
    columnNames = keyConfig?.names
  } catch (e) {
    if (!(e instanceof SyntaxError)) throw e
  }
  if (!columnNames) columnNames = danoniKeysConfig[keyKind]?.names ?? []
  if (columnNames.length <= 0) return
  const mappedColumns = columnNames.map(e => chartObj.orderedCols.value.find(x => x.name == e))

  let currentEventLine
  let currentLabelStartIndex
  for (let pageIndex = 0; pageIndex < dataObj?.scores?.length ?? 0; pageIndex++) {
    const page = dataObj.scores[pageIndex]
    const p = labelMap.get(pageIndex + 1)
    if (p != null) {
      currentEventLine = p
      currentLabelStartIndex = pageIndex
    }
    const pageCount = pageIndex - currentLabelStartIndex

    if (currentEventLine == null || currentEventLine?.pageBlockNum == null || currentEventLine?.pageBlockNum <= 0) continue
    page?.speeds?.forEach(s => {
      let { position, value, type } = s
      if (isNaN(position) || isNaN(value) || !['speed', 'boost'].includes(type)) return
      position = (Number(position) / 48 + currentEventLine.pageBlockNum * pageCount) * 60 / currentEventLine.bpm + currentEventLine.time
      value = Number(value)

      const eventLine = chartObj.orderedEventLines.value.find(e => e.time == position)
      if (eventLine) {
        chartObj.setEventLineParameter(eventLine.eventLineId, type, value)
      } else {
        const t = {}
        t[type] = value
        chartObj.addEventLine(position, EventLineType.Generic, t)
      }
    })

    page?.notes?.forEach((colNotes, i) => {
      const col = mappedColumns[i]
      if (col == null) return
      for (const n of colNotes) {
        if (isNaN(n)) continue
        const t = (Number(n) / 48 + currentEventLine.pageBlockNum * pageCount) * 60 / currentEventLine.bpm + currentEventLine.time
        const [num, den] = timeToFraction(currentEventLine.bpm, t - currentEventLine.time)
        chartObj.addNote(currentEventLine, col.columnIndex, num, den)
      }
    })


    page?.freezes?.forEach((colNotes, i) => {
      const col = mappedColumns[i]
      if (col == null) return
      for (const n of colNotes) {
        if (isNaN(n)) continue
        const t = (Number(n) / 48 + currentEventLine.pageBlockNum * pageCount) * 60 / currentEventLine.bpm + currentEventLine.time
        const [num, den] = timeToFraction(currentEventLine.bpm, t - currentEventLine.time)
        chartObj.addNote(currentEventLine, col.columnIndex, num, den, { long: 'x' })
      }
    })

  }



}
